import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatDisplayDate } from "@/lib/dateFormat";
import type { HealthScoreBreakdown } from "@/pages/dashboard/fiscalHealthScore";
import type { FiscalAgendaObligation } from "@/services/fiscalService";

const TAX_I18N: Record<string, string> = {
  IVA: "fiscalAgenda.tax.iva",
  IRAE: "fiscalAgenda.tax.irae",
  BPS: "fiscalAgenda.tax.bps",
  IRPF: "fiscalAgenda.tax.irpf",
  IRNR: "fiscalAgenda.tax.irnr",
  MONOTRIBUTO: "fiscalAgenda.tax.monotributo",
};

interface FiscalHealthExplanationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  breakdown: HealthScoreBreakdown;
  overdueObligations?: FiscalAgendaObligation[];
  dueTodayObligations?: FiscalAgendaObligation[];
  dueIn1Obligations?: FiscalAgendaObligation[];
  dueIn3Obligations?: FiscalAgendaObligation[];
}

export function FiscalHealthExplanationModal({
  open,
  onOpenChange,
  breakdown,
  overdueObligations = [],
  dueTodayObligations = [],
  dueIn1Obligations = [],
  dueIn3Obligations = [],
}: FiscalHealthExplanationModalProps): JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { score, penalties, fiscalApiFactors } = breakdown;
  const isOnEstadoFiscalPage = pathname === "/fiscal-status" || pathname.startsWith("/fiscal-status/");

  const factors: { key: string; count: number }[] = [];
  if (fiscalApiFactors) {
    if (fiscalApiFactors.overdueCount > 0) {
      factors.push({
        key: "dashboard.fiscalHealthModal.factorOverdue",
        count: fiscalApiFactors.overdueCount,
      });
    }
    if (fiscalApiFactors.dueIn3Count > 0) {
      factors.push({
        key: "dashboard.fiscalHealthModal.factorDueIn3",
        count: fiscalApiFactors.dueIn3Count,
      });
    }
    if (fiscalApiFactors.pendingAckOverdueCount > 0) {
      factors.push({
        key: "dashboard.fiscalHealthModal.factorPendingAckOverdue",
        count: fiscalApiFactors.pendingAckOverdueCount,
      });
    }
    if (fiscalApiFactors.pendingAckDueSoonCount > 0) {
      factors.push({
        key: "dashboard.fiscalHealthModal.factorPendingAckDueSoon",
        count: fiscalApiFactors.pendingAckDueSoonCount,
      });
    }
    if (fiscalApiFactors.pendingAckFreshCount > 0) {
      factors.push({
        key: "dashboard.fiscalHealthModal.factorPendingAckFresh",
        count: fiscalApiFactors.pendingAckFreshCount,
      });
    }
    if (fiscalApiFactors.currentMonthComplete && factors.length === 0) {
      factors.push({
        key: "dashboard.fiscalHealthModal.factorCurrentMonthComplete",
        count: 1,
      });
    }
  } else {
    if (penalties.overdue > 0) {
      factors.push({
        key: "dashboard.fiscalHealthModal.factorOverdue",
        count: penalties.overdue,
      });
    }
    if (penalties.dueIn7Days > 0) {
      factors.push({
        key: "dashboard.fiscalHealthModal.factorDueSoon",
        count: penalties.dueIn7Days,
      });
    }
  }
  if (!fiscalApiFactors && penalties.noCae > 0) {
    factors.push({
      key: "dashboard.fiscalHealthModal.factorNoCae",
      count: 1,
    });
  }
  if (!fiscalApiFactors && penalties.caeLow > 0) {
    factors.push({
      key: "dashboard.fiscalHealthModal.factorCaeLow",
      count: 1,
    });
  }
  if (!fiscalApiFactors && penalties.noInvoicesThisMonth > 0) {
    factors.push({
      key: "dashboard.fiscalHealthModal.factorNoInvoices",
      count: 1,
    });
  }

  const otherFactors = factors.filter(
    (f) =>
      f.key !== "dashboard.fiscalHealthModal.factorOverdue" &&
      f.key !== "dashboard.fiscalHealthModal.factorDueToday" &&
      f.key !== "dashboard.fiscalHealthModal.factorDueIn1" &&
      f.key !== "dashboard.fiscalHealthModal.factorDueIn3"
  );
  const hasObligationLists =
    overdueObligations.length > 0 ||
    dueTodayObligations.length > 0 ||
    dueIn1Obligations.length > 0 ||
    dueIn3Obligations.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("dashboard.fiscalHealthModal.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">
              {t("dashboard.fiscalHealthModal.scoreLabel")}
            </p>
            <p className="text-2xl font-semibold tabular-nums text-foreground">
              {score} / 100
            </p>
            <p className="pt-2 text-sm text-muted-foreground">
              {t("dashboard.fiscalHealthModal.scoreMeaning")}
            </p>
          </div>

          <div className="space-y-4">
            <p className="text-sm font-medium text-foreground">
              {t("dashboard.fiscalHealthModal.intro")}
            </p>

          {dueTodayObligations.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                {t("dashboard.fiscalHealthModal.factorDueToday")}
                {dueTodayObligations.length > 1 &&
                  ` (${dueTodayObligations.length})`}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("dashboard.fiscalHealthModal.factorDueTodayHelp")}
              </p>
              <ul className="list-none space-y-2 text-sm text-muted-foreground">
                {dueTodayObligations.map((ob) => (
                  <li
                    key={ob.id}
                    className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded border border-[var(--fx-ink-200)] bg-[rgba(232,196,144,0.16)] px-2 py-1.5 dark:border-[rgba(232,196,144,0.22)] dark:bg-[rgba(232,196,144,0.10)]"
                  >
                    <span className="font-medium text-foreground">
                      {t(TAX_I18N[ob.taxType] ?? ob.taxType)}
                    </span>
                    <span>{ob.period}</span>
                    <span>{formatDisplayDate(ob.dueDate, { useCalendarDate: true })}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {overdueObligations.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                {t("dashboard.fiscalHealthModal.factorOverdue")}
                {overdueObligations.length > 1 &&
                  ` (${overdueObligations.length})`}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("dashboard.fiscalHealthModal.factorOverdueHelp")}
              </p>
              <ul className="list-none space-y-2 text-sm text-muted-foreground">
                {overdueObligations.map((ob) => (
                  <li
                    key={ob.id}
                    className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded border border-[var(--fx-ink-200)] bg-[rgba(201,94,62,0.08)] px-2 py-1.5 dark:border-[rgba(201,94,62,0.24)] dark:bg-[rgba(201,94,62,0.12)]"
                  >
                    <span className="font-medium text-foreground">
                      {t(TAX_I18N[ob.taxType] ?? ob.taxType)}
                    </span>
                    <span>{ob.period}</span>
                    <span>{formatDisplayDate(ob.dueDate, { useCalendarDate: true })}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {dueIn1Obligations.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                {t("dashboard.fiscalHealthModal.factorDueIn1")}
                {dueIn1Obligations.length > 1 &&
                  ` (${dueIn1Obligations.length})`}
              </p>
              <ul className="list-none space-y-2 text-sm text-muted-foreground">
                {dueIn1Obligations.map((ob) => (
                  <li
                    key={ob.id}
                    className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded border border-[var(--fx-ink-200)] bg-[rgba(232,196,144,0.16)] px-2 py-1.5 dark:border-[rgba(232,196,144,0.22)] dark:bg-[rgba(232,196,144,0.10)]"
                  >
                    <span className="font-medium text-foreground">
                      {t(TAX_I18N[ob.taxType] ?? ob.taxType)}
                    </span>
                    <span>{ob.period}</span>
                    <span>{formatDisplayDate(ob.dueDate, { useCalendarDate: true })}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {dueIn3Obligations.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                {t("dashboard.fiscalHealthModal.factorDueIn3")}
                {dueIn3Obligations.length > 1 &&
                  ` (${dueIn3Obligations.length})`}
              </p>
              <ul className="list-none space-y-2 text-sm text-muted-foreground">
                {dueIn3Obligations.map((ob) => (
                  <li
                    key={ob.id}
                    className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded border border-[var(--fx-ink-200)] bg-[rgba(232,196,144,0.16)] px-2 py-1.5 dark:border-[rgba(232,196,144,0.22)] dark:bg-[rgba(232,196,144,0.10)]"
                  >
                    <span className="font-medium text-foreground">
                      {t(TAX_I18N[ob.taxType] ?? ob.taxType)}
                    </span>
                    <span>{ob.period}</span>
                    <span>{formatDisplayDate(ob.dueDate, { useCalendarDate: true })}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!hasObligationLists &&
            (factors.length > 0 ? (
              <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                {factors.map(({ key, count }) => (
                  <li key={key}>
                    {t(key)}
                    {count > 1 && ` (${count})`}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("dashboard.fiscalHealthModal.noPenalties")}
              </p>
            ))}

          {hasObligationLists && otherFactors.length > 0 && (
            <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
              {otherFactors.map(({ key, count }) => (
                <li key={key}>
                  {t(key)}
                  {count > 1 && ` (${count})`}
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap gap-2 border-t border-border pt-4">
            {!isOnEstadoFiscalPage && (
              <Button variant="outline" size="sm" asChild className="w-fit">
                <Link to="/fiscal-status">
                  {t("dashboard.fiscalHealthModal.viewEstadoFiscal")}
                </Link>
              </Button>
            )}
            {(overdueObligations.length > 0 ||
              dueTodayObligations.length > 0 ||
              dueIn1Obligations.length > 0 ||
              dueIn3Obligations.length > 0) && (
              <Button variant="outline" size="sm" asChild className="w-fit">
                <Link to="/agenda">
                  {t("dashboard.fiscalHealthModal.viewInAgenda")}
                </Link>
              </Button>
            )}
            {fiscalApiFactors &&
              (fiscalApiFactors.pendingAckOverdueCount > 0 ||
                fiscalApiFactors.pendingAckDueSoonCount > 0 ||
                fiscalApiFactors.pendingAckFreshCount > 0) && (
                <Button variant="outline" size="sm" asChild className="w-fit">
                  <Link to="/invoices/recibidas?pendingAck=1">
                    {t(
                      "dashboard.fiscalHealthModal.viewPendingAck",
                      "Revisar comprobantes sin responder",
                    )}
                  </Link>
                </Button>
              )}
          </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
